import React, { useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BackLink } from 'ui/BackLink';
import { isAdmin, isFinanceUser, isSr } from 'store/modules/auth/selectors';
import { IBankTransaction, isCredit, IBankTransactionDetailLocationState } from './types';
import { formatPriceCents, formatDateDisplay } from 'utils';
import { TextInput } from 'ui/TextInput';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { useBankTransaction } from './hooks/useBankTransactionQueries';
import { AuditInfo } from './components/AuditInfo';
import { BankTransactionCommentsSection } from './components/BankTransactionCommentsSection';
import { useNotifications } from 'hooks/useNotifications';

interface PaymentPageParams {
  uuid: string;
}

export const PaymentPage: React.FC = () => {
  const { uuid } = useParams<PaymentPageParams>();
  const history = useHistory();
  const location = useLocation<IBankTransactionDetailLocationState>();
  const userIsAdmin = useSelector(isAdmin);
  const userIsFinance = useSelector(isFinanceUser);
  const userIsSr = useSelector(isSr);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  // Get return URL from location state, or fall back to base URL
  const backUrl = location.state?.returnUrl || '/finance/bank-transactions';

  const canViewPayment = userIsAdmin || userIsFinance || userIsSr;

  useEffect(() => {
    if (!canViewPayment) {
      // Redirect unauthorized users
      history.push('/');
    }
  }, [canViewPayment, history]);

  // Fetch payment data from API
  const { data: response, isLoading, isError } = useBankTransaction(uuid, canViewPayment);

  const payment: IBankTransaction | undefined = response?.data;

  if (!canViewPayment) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="payment-page mx-[20px] crm-desktop:mx-[80px]">
        <BackLink type="internal" href={backUrl}>
          Back
        </BackLink>
        <div className="mt-20px text-center">
          <LoadingBar label="Loading payment..." />
        </div>
      </div>
    );
  }

  if (isError || !payment) {
    return (
      <div className="payment-page mx-[20px] crm-desktop:mx-[80px]">
        <BackLink type="internal" href={backUrl}>
          Back
        </BackLink>
        <div className="mt-20px text-center">
          <span className="font-pt-sans text-15px text-gray-100">Payment not found</span>
        </div>
      </div>
    );
  }

  // Validate that this is a debit transaction
  if (isCredit(payment.amountCents)) {
    // Redirect credit transactions to receipt page
    history.replace(`/finance/bank-transactions/receipt/${uuid}`);
    return null;
  }

  return (
    <div className="payment-page mx-[20px] crm-desktop:mx-[80px]">
      {/* Header with Back and Title */}
      <div className="">
        <BackLink type="internal" href={backUrl}>
          Back
        </BackLink>
      </div>

      {/* Title */}
      <div className="flex items-start justify-between mb-20px">
        <h1 className="m-0 font-noe-display text-[40px] leading-tight font-normal">
          Payment <span className="uppercase">{payment.currency}</span> {formatPriceCents(payment.amountCents)}
        </h1>

        <AuditInfo
          createdAt={payment.createdAt}
          createdBy={payment.createdBy}
          updatedAt={payment.updatedAt}
          updatedBy={payment.updatedBy}
        />
      </div>

      {/* Main Info Section */}
      <div className="font-pt-sans">
        <h2 className="font-bold text-[17px] text-black">Main Info</h2>

        <div className="flex gap-15px">
          <div className="flex-1">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Transaction Date</label>
            <TextInput id="transaction-date" value={formatDateDisplay(payment.transactionDate)} disabled />
          </div>
          <div className="flex-1">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Value Date</label>
            <TextInput id="value-date" value={formatDateDisplay(payment.valueDate)} disabled />
          </div>
          <div className="w-1/4">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Bank</label>
            <TextInput id="bank" value={payment.paymentMethodGroup.name} disabled />
          </div>
          <div className="w-[50px]">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Currency</label>
            <TextInput id="currency" value={payment.currency} disabled />
          </div>
          <div className="flex-1">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Amount</label>
            <TextInput id="amount" value={formatPriceCents(payment.amountCents)} disabled />
          </div>
          <div className="w-1/5">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Reference</label>
            <TextInput id="reference" value={payment.reference || ''} disabled />
          </div>
        </div>
        {/* Narration - Full Width */}
        <div className="pt-20px font-pt-sans">
          <div className="pb-10px text-[14px] text-black">Narration</div>
          <div className="text-[15px] whitespace-pre-wrap">{payment.narration}</div>
        </div>
      </div>
      <BankTransactionCommentsSection transactionUuid={uuid} showSuccessNotification={showSuccessNotification} showErrorNotification={showErrorNotification}/>
    </div>
  );
};
